import {formatMoney} from '@shopify/theme-currency';
import FlickityFade from 'flickity-fade';

import QuantityCounter from '../globals/quantity-handle';
import hideElement from '../util/hide-element';
import showElement from '../util/show-element';
import {ProductForm} from '../vendor/theme-scripts/theme-product-form';

const selectors = {
  product: '[data-product]',
  productForm: '[data-product-form]',
  addToCart: '[data-add-to-cart]',
  addToCartText: '[data-add-to-cart-text]',
  comparePrice: '[data-compare-price]',
  comparePriceText: '[data-compare-text]',
  formWrapper: '[data-form-wrapper]',
  originalSelectorId: '[data-product-select]',
  priceWrapper: '[data-price-wrapper]',
  productSlideshow: '[data-product-slideshow]',
  productImage: '[data-product-image]',
  productJson: '[data-product-json]',
  productPrice: '[data-product-price]',
  unitPrice: '[data-product-unit-price]',
  unitBase: '[data-product-base]',
  unitWrapper: '[data-product-unit]',
  preOrderTag: '_preorder',
  sliderEnabled: 'flickity-enabled',
  productSlide: '.product__slide',
  dataCartBar: 'data-cart-bar',
  dataTallLayout: 'data-tall-layout',
  dataEnableHistoryState: 'data-enable-history-state',
  dataImageId: 'data-image-id',
  cartBar: '#cart-bar',
};

const classes = {
  hide: 'hide',
  variantSoldOut: 'variant--soldout',
  variantUnavailable: 'variant--unavailabe',
  productPriceSale: 'product__price--sale',
};

class ProductAddForm {
  constructor(section) {
    this.section = section;
    this.container = section.container;
    this.tallLayout = this.container.getAttribute(selectors.dataTallLayout) === 'true';
    this.product = this.container.querySelector(selectors.product);

    // Stop parsing if we don't have the product
    if (!this.product) {
      return;
    }

    this.productForm = this.container.querySelector(selectors.productForm);
    this.enableHistoryState = this.container.getAttribute(selectors.dataEnableHistoryState) === 'true';

    if (this.container.getAttribute(selectors.dataCartBar) === 'true') {
      this.cartBar = this.container.querySelector(selectors.cartBar);
      this.cartBarProductForm = this.cartBar.querySelector(selectors.productForm);
    }

    const counter = new QuantityCounter(this.container);
    counter.init();

    this.init();

    this.hasUnitPricing = this.container.querySelector(selectors.unitWrapper);
  }

  init() {
    let productJSON = null;
    const productElemJSON = this.container.querySelector(selectors.productJson);
    if (productElemJSON) {
      productJSON = productElemJSON.innerHTML;
    }
    if (productJSON) {
      this.productJSON = JSON.parse(productJSON);
      this.linkForm();
    } else {
      console.error('Missing product JSON');
    }
  }

  destroy() {
    this.productForm.destroy();

    if (this.container.getAttribute(selectors.dataCartBar) === 'true') {
      this.cartBarProductForm.destroy();
    }
  }

  linkForm() {
    this.productForm = new ProductForm(this.productForm, this.productJSON, {
      onOptionChange: this.onOptionChange.bind(this),
    });

    if (this.container.getAttribute(selectors.dataCartBar) === 'true') {
      this.cartBarProductForm = new ProductForm(this.cartBarProductForm, this.productJSON, {
        onOptionChange: this.onOptionChange.bind(this),
      });
    }
  }

  onOptionChange(evt) {
    this.updateAddToCartState(evt);
    this.updateProductImage(evt);
    this.updateProductPrices(evt);

    if (this.enableHistoryState) {
      this.updateHistoryState(evt);
    }
  }

  updateAddToCartState(evt) {
    const variant = evt.dataset.variant;
    let addText = theme.strings.addToCart;
    const priceWrapper = this.container.querySelectorAll(selectors.priceWrapper);
    const addToCart = this.container.querySelectorAll(selectors.addToCart);
    const addToCartText = this.container.querySelectorAll(selectors.addToCartText);
    const formWrapper = this.container.querySelectorAll(selectors.formWrapper);

    if (this.productJSON.tags.includes(selectors.preOrderTag)) {
      addText = theme.strings.preOrder;
    }

    if (priceWrapper.length && variant) {
      priceWrapper.forEach((element) => {
        element.classList.remove(classes.hide);
      });
    }

    if (addToCart.length) {
      addToCart.forEach((element) => {
        if (variant) {
          if (variant.available) {
            element.disabled = false;
          } else {
            element.disabled = true;
          }
        } else {
          element.disabled = true;
        }
      });
    }

    if (addToCartText.length) {
      addToCartText.forEach((element) => {
        if (variant) {
          if (variant.available) {
            element.innerHTML = addText;
          } else {
            element.innerHTML = theme.strings.soldOut;
          }
        } else {
          element.innerHTML = theme.strings.unavailable;
        }
      });
    }

    if (formWrapper.length) {
      formWrapper.forEach((element) => {
        if (variant) {
          if (variant.available) {
            element.classList.remove(classes.variantSoldOut, classes.variantUnavailable);
          } else {
            element.classList.add(classes.variantSoldOut);
            element.classList.remove(classes.variantUnavailable);
          }
          const formSelect = element.querySelector(selectors.originalSelectorId);
          if (formSelect) {
            formSelect.value = variant.id;
          }
        } else {
          element.classList.add(classes.variantUnavailable);
          element.classList.remove(classes.variantSoldOut);
        }
      });
    }
  }

  updateHistoryState(evt) {
    const variant = evt.dataset.variant;
    const location = window.location.href;
    if (variant && location.includes('/product')) {
      const url = new window.URL(location);
      const params = url.searchParams;
      params.set('variant', variant.id);
      url.search = params.toString();
      const urlString = url.toString();
      window.history.replaceState({path: urlString}, '', urlString);
    }
  }

  getBaseUnit(variant) {
    return variant.unit_price_measurement.reference_value === 1
      ? variant.unit_price_measurement.reference_unit
      : variant.unit_price_measurement.reference_value + variant.unit_price_measurement.reference_unit;
  }

  updateProductPrices(evt) {
    const variant = evt.dataset.variant;
    const priceWrappers = this.container.querySelectorAll(selectors.priceWrapper);

    priceWrappers.forEach((wrap) => {
      const comparePrice = wrap.querySelector(selectors.comparePrice);
      const productPrice = wrap.querySelector(selectors.productPrice);
      const comparePriceText = wrap.querySelector(selectors.comparePriceText);

      if (variant) {
        productPrice.innerHTML = formatMoney(variant.price, theme.moneyFormat);
      }

      if (variant && variant.compare_at_price > variant.price) {
        comparePrice.innerHTML = formatMoney(variant.compare_at_price, theme.moneyFormat);
        comparePrice.classList.remove(classes.hide);
        comparePriceText.classList.remove(classes.hide);
        productPrice.classList.add(classes.productPriceSale);
      } else if (comparePrice) {
        comparePrice.innerHTML = '';
        comparePrice.classList.add(classes.hide);
        comparePriceText.classList.add(classes.hide);
        productPrice.classList.remove(classes.productPriceSale);
      }
    });

    if (this.hasUnitPricing) {
      this.updateProductUnits(evt);
    }
  }

  updateProductUnits(evt) {
    const variant = evt.dataset.variant;

    if (typeof variant.unit_price !== 'undefined') {
      const price = formatMoney(variant.unit_price, theme.moneyFormat);
      const base = this.getBaseUnit(variant);
      this.container.querySelector(selectors.unitPrice).innerHTML = price;
      this.container.querySelector(selectors.unitBase).innerHTML = base;
      showElement(this.container.querySelector(selectors.unitWrapper));
    } else {
      hideElement(this.container.querySelector(selectors.unitWrapper));
    }
  }

  updateProductImage(evt) {
    const variant = evt.dataset.variant;

    if (variant) {
      // Update variant image, if one is set
      if (variant.featured_media) {
        const newImg = this.container.querySelector(`${selectors.productImage}[${selectors.dataImageId}="${variant.featured_media.id}"]`);
        const newImageParent = newImg.closest(selectors.productSlide);
        // If we have a mobile breakpoint or the tall layout is disabled,
        // just switch the slideshow.
        if (newImageParent) {
          const newImagePos = Array.from(newImageParent.parentElement.children).indexOf(newImageParent);
          const slider = this.container.querySelector(selectors.productSlideshow);

          if (slider && slider.classList.contains(selectors.sliderEnabled)) {
            const flkty = FlickityFade.data(slider).select(newImagePos);
          }

          if (!theme.variables.bpSmall && this.tallLayout) {
            // We know its a tall layout, if it's sticky
            // scroll to the images
            // Scroll to/reorder image unless it's the first photo on load

            if (newImagePos !== 0) {
              // Scroll to variant image
              window.scrollTo({
                top: newImg.getBoundingClientRect().top + window.scrollY,
                left: 0,
                behavior: 'smooth',
              });
            }
          }
        }
      }
    }
  }
}

const productFormSection = {
  onLoad() {
    this.section = new ProductAddForm(this);
  },
  onUnload: function () {
    if (typeof this.section.unload === 'function') {
      this.section.unload();
    }
  },
};

export {ProductAddForm, productFormSection};
